import { useMemo, useState } from "react";
import { createFileRoute, useNavigate, Navigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiError } from "@njbsoft/api-client";
import { Breadcrumbs, PageHeader, PageSkeleton } from "@/components/ui";
import { useToast } from "@/hooks/use-toast";
import { useActiveMembership, useMyCompanyContext } from "@/hooks/use-company-context";
import { isCompanyAdminRole } from "@/hooks/use-company-role";
import {
  memberPickerCapped,
  MEMBER_PICKER_PARAMS,
  useCompanyMembers,
} from "@/hooks/use-company-members";
import { kitHolderOptions, useCreateTestKit, type KitWrite } from "@/hooks/use-company-test-kits";
import { KitForm } from "@/components/kits/kit-form";

export const Route = createFileRoute("/_company/company/kits/new")({
  component: NewKitPage,
});

function NewKitPage() {
  const { t } = useTranslation(["company", "common"]);
  const navigate = useNavigate();
  const toasts = useToast();
  const contextQuery = useMyCompanyContext();
  const active = useActiveMembership();
  const companyId = active?.company_id ?? "";
  const createMutation = useCreateTestKit(companyId);
  const membersQuery = useCompanyMembers(companyId, MEMBER_PICKER_PARAMS);
  const holders = useMemo(
    () => kitHolderOptions(membersQuery.data?.items ?? []),
    [membersQuery.data?.items],
  );
  const holdersCapped = memberPickerCapped(membersQuery.data?.items);
  const [serialConflict, setSerialConflict] = useState(false);
  const [holderNotMember, setHolderNotMember] = useState(false);

  // Creating a kit is admin-only, and this is a directly navigable route, so
  // the guard lives here rather than in `beforeLoad` — which runs outside
  // React and cannot read the membership. Never redirect on unresolved data:
  // an admin deep-linking cold would be bounced off their own form.
  if (contextQuery.isLoading) {
    return (
      <div className="mx-auto max-w-3xl p-8">
        <PageSkeleton />
      </div>
    );
  }
  if (!isCompanyAdminRole(active?.role)) return <Navigate to="/company/kits" replace />;

  const handleSubmit = (body: KitWrite) => {
    setSerialConflict(false);
    setHolderNotMember(false);
    createMutation.mutate(body, {
      onSuccess: (created) => {
        toasts.success("company:kits.saveSuccess");
        void navigate({ to: "/company/kits/$kitId", params: { kitId: created.id } });
      },
      onError: (err) => {
        const code = err instanceof ApiError ? err.code : undefined;
        if (code === "kit_serial_conflict_for_owner") {
          setSerialConflict(true);
          return;
        }
        // The named holder works somewhere else: one field to change, so it
        // reads on the picker rather than as a toast.
        if (code === "holder_not_member") {
          setHolderNotMember(true);
          return;
        }
        toasts.apiError(code);
      },
    });
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-8">
      <Breadcrumbs
        items={[
          { label: t("company:kits.title"), to: "/company/kits" },
          { label: t("company:kits.newKit") },
        ]}
      />
      <PageHeader title={t("company:kits.newKit")} />
      <KitForm
        mode="create"
        isSubmitting={createMutation.isPending}
        serialConflict={serialConflict}
        // Reaching this form at all means the caller is an admin (the guard
        // above), and only an admin may name a holder.
        canChangeHolder
        holders={holders}
        holdersCapped={holdersCapped}
        holderNotMember={holderNotMember}
        onSubmit={handleSubmit}
        onCancel={() => void navigate({ to: "/company/kits" })}
      />
    </div>
  );
}
